import React from "react";
import { Link } from "react-router-dom";
import { FileText, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

const statusLabels = {
  draft: "Draft", under_review: "Under Review", pending_approval: "Pending Approval",
  approved: "Approved", active: "Active", expired: "Expired",
  terminated: "Terminated", archived: "Archived",
};

const statusColors = {
  draft: "bg-slate-100 text-slate-700", under_review: "bg-amber-100 text-amber-700",
  pending_approval: "bg-blue-100 text-blue-700", approved: "bg-emerald-100 text-emerald-700",
  active: "bg-cyan-100 text-cyan-700", expired: "bg-red-100 text-red-700",
  terminated: "bg-gray-100 text-gray-700", archived: "bg-purple-100 text-purple-700",
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);

export function RecentContracts({ contracts }) {
  return (
    <Card data-testid="recent-contracts">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-mono uppercase tracking-wider text-slate-500">Recent Contracts</CardTitle>
        <Link to="/contracts">
          <Button variant="ghost" size="sm" className="gap-1 text-slate-600 hover:text-slate-900">View All <ArrowRight size={14} /></Button>
        </Link>
      </CardHeader>
      <CardContent>
        {contracts?.length > 0 ? (
          <div className="space-y-3">
            {contracts.map((contract) => (
              <Link
                key={contract.id}
                to={`/contracts/${contract.id}`}
                className="flex items-center justify-between p-3 rounded-sm hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                data-testid={`recent-contract-${contract.id}`}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <FileText size={18} className="text-slate-500" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">{contract.title}</p>
                    <p className="text-sm text-slate-500">{contract.contracting_party?.legal_name || "No party specified"}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Badge className={`rounded-sm ${statusColors[contract.status] || "bg-slate-100 text-slate-700"}`}>
                    {statusLabels[contract.status] || contract.status}
                  </Badge>
                  {contract.contract_value && (
                    <span className="text-sm font-mono text-slate-600 dark:text-slate-400">{formatCurrency(contract.contract_value)}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-400">
            <FileText size={40} className="mx-auto mb-3 opacity-50" />
            <p>No contracts yet</p>
            <Link to="/contracts/new/wizard">
              <Button variant="link" className="text-cyan-600 mt-2">Create your first contract</Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
